import Example from './example/Example';
import React, { useEffect } from 'react';
import { PageSEO } from '../common/blog/elements/SEO';
import PageLayout from '../common/page-layout/PageLayout';
import { CloudArrowUpIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import { useRouter } from 'next/router';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import Image from 'next/image';
import orangeLogo from 'public/orange-logo.png';
import logoSettings from 'public/logo-settings.png';
import heroImage from 'public/anki_hero_screenshot.png';
import ankiButtonsImage from 'public/anki-buttons.png';

import nervousSystemDefaultInstructionsImage from 'public/anki-example-default-instructions.png';
import nervousSystemDefaultBeforeImage from 'public/anki-example-5-before.png';
import nervousSystemDefaultAfterImage from 'public/anki-example-5-after.png';

import nervousSystemInstructionsImage from 'public/anki-example-6-instructions.png';
import nervousSystemBeforeImage from 'public/anki-example-5-before.png';
import nervousSystemAfterImage from 'public/anki-example-6-after.png';

import spanishWordsInstructionsImage from 'public/anki-example-9-instructions.png';
import spanishWordsBeforeImage from 'public/anki-example-9-before.png';
import spanishWordsAfterImage from 'public/anki-example-9-after.png';

import gallbladderInstructionsImage from 'public/anki-example-image-instructions.png';
import gallbladderBeforeImage from 'public/anki-example-7-before.png';
import gallbladderAfterImage from 'public/anki-example-7-after.png';

const features = [
  {
    name: 'The generate button.',
    description:
      'Click this button to instantly generate occlusions. Any existing occlusions will be untouched.',
    icon: CloudArrowUpIcon,
    image: orangeLogo
  },
  {
    name: 'The settings button.',
    description:
      'Click this button to view and edit your custom instructions or link your Limbiks account.',
    icon: LockClosedIcon,
    image: logoSettings
  }
];

export default function AnkiImageOcclusion() {
  const router = useRouter();

  const { trackEvent, analyticsInstance } = useAnalytics();

  useEffect(() => {
    if (router.pathname.includes('/examples')) {
      setTimeout(() => {
        scrollToExamples();
      }, 1000);
    }
  }, []);

  const onExamplesClick = () => {
    trackEvent(analyticsInstance, 'anki_occlusion_click_hero_examples');
    scrollToExamples();
  };

  const scrollToExamples = () => {
    var element_to_scroll_to = document.getElementById('examples');
    const y =
      element_to_scroll_to.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({
      top: y,
      behavior: 'smooth'
    });
  };

  return (
    <PageLayout>
      <>
        <PageSEO
          title={'Anki - AI Image Occlusion'}
          description={
            'AI Image Occlusion Anki-Addon generates all of your occlusions in seconds'
          }
        />{' '}
        <div className="pt-24">
          <section className="border-b bg-white">
            <div className="relative isolate overflow-hidden bg-white">
              <svg
                aria-hidden="true"
                className="absolute inset-0 -z-10 h-full w-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
              >
                <defs>
                  <pattern
                    x="50%"
                    y={-1}
                    id="0787a7c5-978c-4f66-83c7-11c213f99cb7"
                    width={200}
                    height={200}
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M.5 200V.5H200" fill="none" />
                  </pattern>
                </defs>
                <rect
                  fill="url(#0787a7c5-978c-4f66-83c7-11c213f99cb7)"
                  width="100%"
                  height="100%"
                  strokeWidth={0}
                />
              </svg>
              <div className="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:px-8 lg:py-40">
                <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-8">
                  <Image
                    alt="Limbiks"
                    src={orangeLogo}
                    className="h-[44px] w-[44px]"
                  />
                  <div className="mt-24 sm:mt-32 lg:mt-16">
                    <div className="flex">
                      <div className="relative flex items-center gap-x-4 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10">
                        <span className="font-semibold text-primary">
                          AI Image Occlusion Anki Add-on
                        </span>
                        <span
                          aria-hidden="true"
                          className="h-4 w-px bg-gray-900/10"
                        />
                        <div className="flex items-center gap-x-1">
                          681543712
                        </div>
                      </div>
                    </div>
                  </div>
                  <h1 className="mt-10 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                    Stop wasting time drawing boxes
                  </h1>
                  <p className="mt-6 text-lg leading-8 text-gray-600">
                    The AI Image Occlusion Anki Add-on generates all of your
                    occlusions in seconds. Use custom instructions to occlude
                    only what you need.
                  </p>
                  <div className="mt-10 flex items-center gap-x-6">
                    <a
                      onClick={() => {
                        trackEvent(
                          analyticsInstance,
                          'anki_occlusion_click_hero_get'
                        );
                      }}
                      href="https://ankiweb.net/shared/info/681543712"
                      target="_blank"
                      className="cursor-pointer rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Get the Add-on
                    </a>
                    <button
                      onClick={onExamplesClick}
                      className="text-sm font-semibold leading-6 text-gray-900"
                    >
                      See examples <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
                <div className="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
                  <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
                    <div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
                      <Image
                        alt="App screenshot"
                        src={heroImage}
                        className="w-[56rem] rounded-md shadow-2xl ring-1 ring-gray-900/10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="overflow-hidden bg-white py-24 sm:py-32">
              <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
                  <div className="lg:ml-auto lg:pl-4 lg:pt-4">
                    <div className="lg:max-w-lg">
                      <h2 className="text-base font-semibold leading-7 text-primary">
                        Easy to use
                      </h2>
                      <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        How it works
                      </p>
                      <p className="mt-6 text-lg leading-8 text-gray-600">
                        Two buttons will be added to your card editor. They will
                        appear when you are adding new cards and editing
                        existing cards.
                      </p>
                      <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-600 lg:max-w-none">
                        {features.map((feature) => (
                          <div key={feature.name} className="relative pl-9">
                            <dt className="inline font-semibold text-gray-900">
                              <Image
                                alt={feature.name}
                                className="absolute left-1 top-1 h-5 w-5"
                                src={feature.image}
                              />

                              {feature.name}
                            </dt>{' '}
                            <dd className="inline">{feature.description}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                  <div className="flex items-start justify-end lg:order-first">
                    <Image
                      alt="Product screenshot"
                      src={ankiButtonsImage}
                      className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-gray-400/10 sm:w-[57rem]"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              id="examples"
              className="container mx-auto my-2 mb-2 flex max-w-5xl flex-col gap-5 pl-8 pr-8 md:my-8"
            >
              <h1 className="mb-4 w-full px-5 text-center text-4xl font-bold leading-tight text-gray-800">
                Examples
              </h1>
              <h1 className="mb-8 w-full px-5 text-center text-lg text-gray-800">
                Below are example image occlusions generated by the Anki Add-on.
                Also included is the description used to customize the
                occlusions generated.
              </h1>
              <Example
                title="Nervous System - Default"
                instructionsImage={nervousSystemDefaultInstructionsImage}
                beforeImage={nervousSystemDefaultBeforeImage}
                afterImage={nervousSystemDefaultAfterImage}
                displayImageWidth={430}
                displayImageHeight={246}
              />

              <Example
                title="Nervous System - Nerves of the legs"
                instructionsImage={nervousSystemInstructionsImage}
                beforeImage={nervousSystemBeforeImage}
                afterImage={nervousSystemAfterImage}
                displayImageWidth={430}
                displayImageHeight={246}
              />

              <Example
                title="Spanish Words - Parts of speech"
                instructionsImage={spanishWordsInstructionsImage}
                beforeImage={spanishWordsBeforeImage}
                afterImage={spanishWordsAfterImage}
                displayImageWidth={430}
                displayImageHeight={246}
              />

              <Example
                title="Gallbladder - Image Labels"
                instructionsImage={gallbladderInstructionsImage}
                beforeImage={gallbladderBeforeImage}
                afterImage={gallbladderAfterImage}
                displayImageWidth={430}
                displayImageHeight={246}
              />
            </div>
          </section>
          <div className="bg-white">
            <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:flex lg:items-center lg:justify-between lg:px-8">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Ready to give it a try?
                <br />
                Sign up today.
              </h2>
              <div className="mt-10 flex items-center gap-x-6 lg:mt-0 lg:flex-shrink-0">
                <a
                  onClick={() => {
                    trackEvent(
                      analyticsInstance,
                      'anki_occlusion_click_footer_get'
                    );
                  }}
                  href="https://ankiweb.net/shared/info/681543712"
                  className="rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Get Add-on
                </a>
                <a
                  href="/about"
                  className="text-sm font-semibold leading-6 text-gray-900"
                >
                  Learn more about Limbiks<span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    </PageLayout>
  );
}
